import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Button,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
  Rating,
  useTheme,
} from '@mui/material';
import {
  Add,
  Remove,
  ShoppingCart,
  LocalShipping,
  Verified,
  Warning,
  Info,
  CheckCircle,
  Error,
  ExpandMore,
  Favorite,
  Share,
  Security,
  LocalPharmacy,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { GiMedicinePills } from 'react-icons/gi';
import useUserStore from '../stores/userStore';
import userService from '../services/userService';
import RelatedProducts from '../components/RelatedProducts';
import useCartGuard from '../hooks/useCartGuard';

const ViewProduct = () => {
  const { id } = useParams();
  const theme = useTheme();
  const { product, fetchProductById, addToCart, plusMinus, fetchCart, fetchCartCount } = useUserStore();
  const { requireAuth } = useCartGuard();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [cartLoading, setCartLoading] = useState(false);
  const [isInCart, setIsInCart] = useState(false);
  const [cartQuantity, setCartQuantity] = useState(0);

  const loadProduct = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      await fetchProductById(id);
      // Check if product is in cart after loading
      await checkCartQuantity(id);
    } catch (err) {
      console.error('Error loading product:', err);
      setError('Failed to load product details');
    } finally {
      setLoading(false);
    }
  }, [id, fetchProductById]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  // Re-check cart quantity when product changes
  useEffect(() => {
    if (product && product.id) {
      checkCartQuantity(product.id);
    }
  }, [product]);

  const checkCartQuantity = async (productId) => {
    try {
      const cartData = await fetchCart();
      console.log('ViewProduct - Cart data:', cartData);
      
      if (cartData && cartData.cart && cartData.cart.cart_items) {
        const cartItem = cartData.cart.cart_items.find(item => 
          item.product && item.product.id === parseInt(productId)
        );
        console.log('ViewProduct - Found cart item:', cartItem, 'for productId:', productId);
        
        const quantity = cartItem ? cartItem.quantity : 0;
        setCartQuantity(quantity);
        setIsInCart(quantity > 0);
        
        console.log('ViewProduct - Set cart quantity:', quantity, 'isInCart:', quantity > 0);
      }
    } catch (err) {
      console.error('Error checking cart quantity:', err);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAddToCart = async () => {
    if (!id) return;
    if (!requireAuth()) return;
    
    setCartLoading(true);
    try {
      await userService.addCart(id);
      // Re-check cart quantity to get accurate count
      await checkCartQuantity(id);
      await fetchCartCount();
      showSnackbar('Product added to cart successfully!', 'success');
    } catch (err) {
      console.error('Error adding to cart:', err);
      showSnackbar('Failed to add product to cart', 'error');
    } finally {
      setCartLoading(false);
    }
  };

  const handleRemoveFromCart = async () => {
    if (!id) return;
    if (!requireAuth()) return;
    
    setCartLoading(true);
    try {
      await userService.removePCart(id);
      // Re-check cart quantity to get accurate count
      await checkCartQuantity(id);
      await fetchCartCount();
      showSnackbar('Product removed from cart', 'info');
    } catch (err) {
      console.error('Error removing from cart:', err);
      showSnackbar('Failed to remove product from cart', 'error');
    } finally {
      setCartLoading(false);
    }
  };

  const handleQuantityChange = async (action) => {
    if (!id) return;
    if (!requireAuth()) return;
    
    console.log('ViewProduct - handleQuantityChange:', action, 'for productId:', id);
    setCartLoading(true);
    try {
      if (action === 'inc') {
        console.log('ViewProduct - Adding to cart...');
        await userService.addCart(id);
      } else if (action === 'dec') {
        console.log('ViewProduct - Removing from cart...');
        await userService.removePCart(id);
      }
      
      // Re-check cart quantity to get accurate count
      console.log('ViewProduct - Re-checking cart quantity...');
      await checkCartQuantity(id);
      await fetchCartCount();
      
      showSnackbar(
        action === 'inc' ? 'Item added to cart!' : 'Item removed from cart!', 
        'success'
      );
    } catch (err) {
      console.error('Error updating cart:', err);
      showSnackbar('Failed to update cart', 'error');
    } finally {
      setCartLoading(false);
    }
  };

  const getProductImages = () => {
    if (!product?.images) return [];
    
    // Handle both array and string formats
    if (Array.isArray(product.images)) {
      // If images is an array, return the URLs directly
      return product.images;
    } else {
      // If images is a string, split and construct paths to local assets
      return product.images.split(',').map(img => `/assets/Drugs Images/${img.trim()}`);
    }
  };

  const parseSafetyAdvise = (safetyAdvise) => {
    if (!safetyAdvise) return {};
    try {
      // Clean up the string by replacing single quotes with double quotes
      const cleanedString = safetyAdvise.replace(/'/g, '"');
      return JSON.parse(cleanedString);
    } catch (error) {
      console.error('Error parsing safety advice:', error);
      return {};
    }
  };

  const getSafetyIcon = (status) => {
    if (status?.includes('SAFE')) {
      return <CheckCircle color="success" />;
    } else if (status?.includes('UNSAFE')) {
      return <Error color="error" />;
    } else if (status?.includes('CAUTION')) {
      return <Warning color="warning" />;
    }
    return <Info color="info" />;
  };

  const getSafetyColor = (status) => {
    if (status?.includes('SAFE')) {
      return 'success';
    } else if (status?.includes('UNSAFE')) {
      return 'error';
    } else if (status?.includes('CAUTION')) {
      return 'warning';
    }
    return 'info';
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container>
        <Alert severity="info" sx={{ mt: 2 }}>
          Product not found
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={4}>
          {/* Product Image */}
          <Grid item xs={12} md={6} sx={{ maxWidth: '400px' }}>
            <Card>
              {getProductImages().length > 0 ? (
                <>
                  <CardMedia
                    component="img"
                    height="400"
                    image={getProductImages()[currentImageIndex]}
                    alt={product.name}
                    sx={{ objectFit: 'contain', p: 2, width: '100%' }}
                  />
                  {getProductImages().length > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, p: 2 }}>
                      {getProductImages().map((img, index) => (
                        <CardMedia
                          key={index}
                          component="img"
                          height="60"
                          width="60"
                          image={img}
                          alt={`${product.name} ${index + 1}`}
                          sx={{ 
                            objectFit: 'contain', 
                            cursor: 'pointer',
                            border: currentImageIndex === index ? 2 : 1,
                            borderColor: currentImageIndex === index ? 'primary.main' : 'divider',
                            borderRadius: 1,
                            opacity: currentImageIndex === index ? 1 : 0.7,
                            minWidth: '60px',
                            maxWidth: '60px'
                          }}
                          onClick={() => setCurrentImageIndex(index)}
                        />
                      ))}
                    </Box>
                  )}
                </>
              ) : (
                <Box
                  sx={{
                    height: 400,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 2,
                    bgcolor: 'grey.50'
                  }}
                >
                  <GiMedicinePills size={350} color={theme.palette.primary.main} />
                </Box>
              )}
            </Card>
          </Grid>

          {/* Product Details */}
          <Grid item xs={12} md={6} sx={{maxWidth: '650px'}}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" component="h1" gutterBottom>
                {product.name}
              </Typography>
              
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {product.manufacturer}
              </Typography>

              {product.salt_composition && (
                <Typography variant="body1" color="text.secondary" paragraph>
                  <strong>Salt Composition:</strong> {product.salt_composition}
                </Typography>
              )}

              {/* Product Details Card */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Verified color="success" />
                  Product Details
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Primary Use:</Typography>
                    <Typography variant="body2">{product.product_info?.primary_use || 'Not specified'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Therapeutic Class:</Typography>
                    <Typography variant="body2">
                      {product.product_info?.fact_box?.split('|')[1]?.split(':')[1] || 'Not specified'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Habit Forming:</Typography>
                    <Typography variant="body2">
                      {product.product_info?.fact_box?.split('|')[0]?.split(':')[1] || 'Not specified'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Storage:</Typography>
                    <Typography variant="body2">{product.product_info?.storage || 'Standard storage'}</Typography>
                  </Box>
                </Box>
              </Paper>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h4" color="primary">
                    ₹{product.best_price || product.price}
                  </Typography>
                  {product.best_price && product.best_price !== product.price && (
                    <>
                      <Typography variant="h6" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                        ₹{product.price}
                      </Typography>
                      <Chip
                        label={`Save ₹${(parseFloat(product.price) - parseFloat(product.best_price)).toFixed(2)}`}
                        color="success"
                        size="small"
                      />
                    </>
                  )}
                </Box>
                {product.prescription_required && (
                  <Chip
                    icon={<Warning />}
                    label="Prescription Required"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Quantity Selector */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Quantity in Cart
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <IconButton
                      onClick={() => handleQuantityChange('dec')}
                      disabled={cartLoading || cartQuantity <= 0}
                    >
                      <Remove />
                    </IconButton>
                    <Typography variant="h6" sx={{ minWidth: 40, textAlign: 'center' }}>
                      {cartQuantity}
                    </Typography>
                    <IconButton 
                      onClick={() => handleQuantityChange('inc')}
                      disabled={cartLoading}
                    >
                      <Add />
                    </IconButton>
                  </Box>
                </Box>
              </Box>

              {/* Cart Actions */}
              <Stack spacing={2} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<Favorite />}
                    sx={{ flex: 1 }}
                  >
                    Wishlist
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Share />}
                    sx={{ flex: 1 }}
                  >
                    Share
                  </Button>
                </Stack>
              </Stack>

              {/* Product Info */}
              {product.product_info && (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Product Information
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Verified color="success" />
                    <Typography variant="body2">Genuine Medicine</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LocalShipping color="primary" />
                    <Typography variant="body2">Free delivery on orders above ₹500</Typography>
                  </Box>
                  {product.product_info.primary_use && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Info color="info" />
                      <Typography variant="body2">
                        <strong>Primary Use:</strong> {product.product_info.primary_use}
                      </Typography>
                    </Box>
                  )}
                  {product.product_info.fact_box && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Info color="info" />
                      <Typography variant="body2">
                        <strong>Fact Box:</strong> {product.product_info.fact_box}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Product Information Accordions */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            Product Information
          </Typography>
          
          <Grid container spacing={2}>
            {/* Introduction & Description */}
            <Grid item xs={12} sx={{ width: '100%' }}>
              <Accordion defaultExpanded sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <AccordionSummary 
                  expandIcon={<ExpandMore />} 
                  sx={{ 
                    width: '100%',
                    '& .MuiAccordionSummary-content': {
                      flex: 1,
                      minWidth: 0,
                      width: '100%'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, width: '100%', minWidth: 0 }}>
                    <Info color="primary" />
                    Introduction & Description
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" paragraph>
                    {product.product_info?.introduction || ''}
                  </Typography>
                  {product.product_info?.description && (
                    <Typography variant="body1" paragraph>
                      {product.product_info.description}
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* How to Use & Benefits */}
            <Grid item xs={12} sx={{ width: '100%' }}>
              <Accordion sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <AccordionSummary 
                  expandIcon={<ExpandMore />} 
                  sx={{ 
                    width: '100%',
                    '& .MuiAccordionSummary-content': {
                      flex: 1,
                      minWidth: 0,
                      width: '100%'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, width: '100%', minWidth: 0 }}>
                    <LocalPharmacy color="primary" />
                    How to Use & Benefits
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>How to Use:</Typography>
                    <Typography variant="body1" paragraph>
                      {product.product_info?.how_to_use || 'Please consult your doctor for usage instructions.'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" gutterBottom>Benefits:</Typography>
                    <Typography variant="body1" paragraph>
                      {product.product_info?.benefits || 'Please consult your doctor for information about benefits.'}
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Side Effects */}
            <Grid item xs={12} sx={{ width: '100%' }}>
              <Accordion sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <AccordionSummary 
                  expandIcon={<ExpandMore />} 
                  sx={{ 
                    width: '100%',
                    '& .MuiAccordionSummary-content': {
                      flex: 1,
                      minWidth: 0,
                      width: '100%'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, width: '100%', minWidth: 0 }}>
                    <Warning color="warning" />
                    Side Effects
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" paragraph>
                    {product.product_info?.common_side_effect || 'Please consult your doctor for information about side effects.'}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Safety Advice */}
            <Grid item xs={12} sx={{ width: '100%' }}>
              <Accordion sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <AccordionSummary 
                  expandIcon={<ExpandMore />} 
                  sx={{ 
                    width: '100%',
                    '& .MuiAccordionSummary-content': {
                      flex: 1,
                      minWidth: 0,
                      width: '100%'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, width: '100%', minWidth: 0 }}>
                    <Security color="primary" />
                    Safety Advice
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
                    Important safety information for different conditions and situations
                  </Typography>
                  {Object.entries(parseSafetyAdvise(product.product_info?.safety_advise)).map(([key, value]) => {
                    const safetyColor = getSafetyColor(value[0]);
                    const borderColor = safetyColor === 'error' ? '#f44336' : 
                                      safetyColor === 'success' ? '#4caf50' : 
                                      safetyColor === 'warning' ? '#ff9800' : '#2196f3';
                    
                    return (
                      <Paper key={key} sx={{ p: 2, mb: 2, borderLeft: 4, borderLeftColor: borderColor }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          {getSafetyIcon(value[0])}
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: borderColor }}>
                            {key}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 1 }}>
                          <Chip 
                            label={value[0]?.trim()} 
                            color={safetyColor} 
                            size="small"
                            variant="filled"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                          {value[1]?.trim()}
                        </Typography>
                      </Paper>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Storage & Additional Info */}
            <Grid item xs={12} sx={{ width: '100%' }}>
              <Accordion sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <AccordionSummary 
                  expandIcon={<ExpandMore />} 
                  sx={{ 
                    width: '100%',
                    '& .MuiAccordionSummary-content': {
                      flex: 1,
                      minWidth: 0,
                      width: '100%'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, width: '100%', minWidth: 0 }}>
                    <LocalShipping color="primary" />
                    Storage & Additional Info
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>Storage Instructions:</Typography>
                    <Typography variant="body1" paragraph>
                      {product.product_info?.storage || 'Store in a cool, dry place away from direct sunlight.'}
                    </Typography>
                  </Box>
                  {product.product_info?.if_miss && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom>If You Miss a Dose:</Typography>
                      <Typography variant="body1" paragraph>
                        {product.product_info.if_miss}
                      </Typography>
                    </Box>
                  )}
                  {product.product_info?.fact_box && (
                    <Box>
                      <Typography variant="h6" gutterBottom>Fact Box:</Typography>
                      <Typography variant="body1" paragraph>
                        {product.product_info.fact_box}
                      </Typography>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Additional Info */}
            <Grid item xs={12} sx={{ width: '100%' }}>
              <Accordion sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                <AccordionSummary 
                  expandIcon={<ExpandMore />} 
                  sx={{ 
                    width: '100%',
                    '& .MuiAccordionSummary-content': {
                      flex: 1,
                      minWidth: 0,
                      width: '100%'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, width: '100%', minWidth: 0 }}>
                    <Verified color="success" />
                    Product Details
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Primary Use:</Typography>
                    <Typography variant="body1" paragraph>
                      {product.product_info?.primary_use || 'Treatment information not available.'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Salt Composition:</Typography>
                    <Typography variant="body1" paragraph>
                      {product.salt_composition || 'Composition details not available.'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" gutterBottom>Manufacturer:</Typography>
                    <Typography variant="body1" paragraph>
                      {product.manufacturer || 'Manufacturer information not available.'}
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </Box>

        {/* Related Products Section */}
        <RelatedProducts 
          productId={id} 
          productType="product"
          title="Similar Brands"
        />
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ViewProduct;
